
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Plan, BillingPeriod } from '@/types/plans';
import { toast } from 'sonner';
import { PLAN_PRICING, PLAN_COMMERCIAL_NAMES, getPlanPrice, centsToReais } from '@/constants/plans';
import { ENABLE_ASAAS } from '@/constants/asaas-pricing';
import { useAsaasCheckout } from '@/hooks/useAsaasCheckout';
import { debugLogger } from '@/utils/debugLogger';

export const useSponsorPlans = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Query para obter dados baseado no provider
  const { data: subscriptionData, refetch: refetchSubscriptionData } = useQuery({
    queryKey: [ENABLE_ASAAS ? 'asaasHistory' : 'stripeHistory', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const functionName = ENABLE_ASAAS ? 'get-asaas-history' : 'get-stripe-history';
      debugLogger.debug('plans', `Fetching ${ENABLE_ASAAS ? 'Asaas' : 'Stripe'} data...`);
      
      const { data, error } = await supabase.functions.invoke(functionName);
      if (error) {
        debugLogger.error('plans', `Error fetching ${ENABLE_ASAAS ? 'Asaas' : 'Stripe'} data:`, error);
        throw error;
      }
      debugLogger.debug('plans', `${ENABLE_ASAAS ? 'Asaas' : 'Stripe'} data received:`, data);
      return data;
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0
  });

  // Get pricing based on provider
  const getPrice = (planType: string, period: BillingPeriod) => {
    const role = 'sponsor';
    return centsToReais(getPlanPrice(planType as any, period, role));
  };

  // Definir os planos para patrocinadores com nomes comerciais
  const plans: Plan[] = [
    {
      id: 'sponsor_free',
      name: PLAN_COMMERCIAL_NAMES.sponsor_free,
      description: 'Recursos básicos para começar',
      prices: { 
        weekly: getPrice('sponsor_free', 'weekly'), 
        monthly: getPrice('sponsor_free', 'monthly'), 
        quarterly: getPrice('sponsor_free', 'quarterly') 
      },
      benefits: [
        '30 visualizações de perfis/dia',
        '3 curtidas/dia',
        '3 novas conversas/dia',
        'Acesso a perfis com 3+ dias',
        'Suporte por email'
      ],
      icon: '⭐',
      highlight: 'Ideal para começar'
    },
    {
      id: 'sponsor_standard',
      name: PLAN_COMMERCIAL_NAMES.sponsor_standard,
      description: 'Para patrocinadores ativos',
      prices: { 
        weekly: getPrice('sponsor_standard', 'weekly'), 
        monthly: getPrice('sponsor_standard', 'monthly'), 
        quarterly: getPrice('sponsor_standard', 'quarterly') 
      },
      benefits: [
        'Visualizações ilimitadas',
        '30 curtidas/dia',
        'Conversas ilimitadas',
        'Acesso a perfis com 1+ dia',
        'Filtros avançados',
        '1 boost/mês (3/trimestre)',
        'Suporte prioritário'
      ],
      icon: '💫',
      highlight: 'Mais popular'
    },
    {
      id: 'sponsor_premium',
      name: PLAN_COMMERCIAL_NAMES.sponsor_premium,
      description: 'Experiência completa de patrocínio',
      prices: { 
        weekly: getPrice('sponsor_premium', 'weekly'), 
        monthly: getPrice('sponsor_premium', 'monthly'), 
        quarterly: getPrice('sponsor_premium', 'quarterly') 
      },
      benefits: [
        'Tudo ilimitado',
        'Acesso imediato a perfis novos',
        'Filtro de verificados',
        'Destaque permanente no feed',
        '4 boosts/mês (12/trimestre)',
        'Comprar boosts adicionais',
        'Suporte prioritário 24/7'
      ],
      isPopular: true,
      badge: 'Mais Completo',
      icon: '🔥',
      highlight: 'Experiência premium completa'
    }
  ];

  // Query para obter o plano atual do usuário
  const { data: currentPlan = 'sponsor_free', refetch: refetchCurrentPlan } = useQuery({
    queryKey: ['currentPlan', user?.id],
    queryFn: async () => {
      if (!user?.id) return 'sponsor_free';

      debugLogger.debug('plans', 'Fetching current plan...');
      const { data, error } = await supabase
        .from('profiles')
        .select('plan_type')
        .eq('id', user.id)
        .single();

      if (error) {
        debugLogger.error('plans', 'Error fetching current plan:', error);
        throw error;
      }
      debugLogger.debug('plans', 'Current plan received:', data?.plan_type);
      return data?.plan_type || 'sponsor_free';
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0
  });

  // Get checkout hooks based on payment provider
  const { createCheckoutSession: createAsaasCheckout, isLoading: asaasLoading, paymentModal } = useAsaasCheckout();

  // Mutation para fazer upgrade do plano baseado no provider
  const upgradeMutation = useMutation({
    mutationFn: async ({ planId, billingPeriod }: { planId: string; billingPeriod: BillingPeriod }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const selectedPlan = plans.find(p => p.id === planId);
      if (!selectedPlan) throw new Error('Plan not found');

      debugLogger.info('plans', `Starting ${ENABLE_ASAAS ? 'Asaas' : 'Stripe'} upgrade...`, { planId, billingPeriod });

      if (ENABLE_ASAAS) {
        // Para planos gratuitos, fazer upgrade direto com Asaas
        if (selectedPlan.prices[billingPeriod] === 0) {
          debugLogger.info('plans', 'Free plan upgrade, using Asaas sync approach...');
          
          const { data, error } = await supabase.functions.invoke('sync-asaas-data');
          if (error) throw error;
          
          return { immediate: true, redirect_url: null };
        }

        // Para planos pagos, usar create-asaas-checkout
        await createAsaasCheckout(planId, billingPeriod);
        return { success: true, provider: 'asaas' };
      } else {
        // Para planos gratuitos, fazer upgrade direto sem Stripe
        if (selectedPlan.prices[billingPeriod] === 0) {
          debugLogger.info('plans', 'Free plan upgrade, using sync approach...');
          
          // Chamar sync-stripe-data para garantir que estamos atualizados
          await supabase.functions.invoke('sync-stripe-data');
          
          return { immediate: true, redirect_url: null };
        }

        // Para planos pagos, usar create-checkout-session (Stripe-First)
        const { data, error } = await supabase.functions.invoke('create-checkout-session', {
          body: {
            planId,
            billingPeriod
          }
        });

        if (error) {
          debugLogger.error('plans', 'Error creating checkout session:', error);
          throw error;
        }

        debugLogger.info('plans', 'Checkout session created:', data);

        // Redirecionar para Stripe Checkout
        if (data.url) {
          window.open(data.url, '_blank');
        }

        return data;
      }
    },
    onSuccess: (data, variables) => {
      const planName = plans.find(p => p.id === variables.planId)?.name;
      
      if (ENABLE_ASAAS) {
        if (data?.immediate) {
          toast.success(`Plano ${planName} ativado com sucesso!`);
          // Invalidar queries relacionadas
          queryClient.invalidateQueries({ queryKey: ['currentPlan'] });
          queryClient.invalidateQueries({ queryKey: ['asaasHistory'] });
        } else if (data?.success) {
          toast.success(`Redirecionando para checkout do plano ${planName}...`);
        }
      } else {
        if (data?.immediate) {
          toast.success(`Plano ${planName} ativado com sucesso!`);
          // Invalidar queries relacionadas
          queryClient.invalidateQueries({ queryKey: ['currentPlan'] });
          queryClient.invalidateQueries({ queryKey: ['stripeHistory'] });
        } else if (data?.url) {
          toast.success(`Redirecionando para checkout do plano ${planName}...`);
        } else {
          toast.success(`Iniciando upgrade para o plano ${planName}...`);
        }
      }
    },
    onError: (error: any) => {
      debugLogger.error('plans', 'Error upgrading plan:', error);
      toast.error(error.message || 'Erro ao alterar plano. Tente novamente.');
    }
  });

  // Função para calcular economias com validações robustas
  const calculateSavings = (prices: any) => {
    // Validações de entrada
    if (!prices || typeof prices !== 'object') {
      console.warn('calculateSavings: preços inválidos', prices);
      return {
        monthly: { percentage: 0, amount: 0 },
        quarterly: { percentage: 0, amount: 0 }
      };
    }

    // Garantir que todos os preços sejam números válidos
    const weeklyPrice = Number(prices.weekly) || 0;
    const monthlyPrice = Number(prices.monthly) || 0;
    const quarterlyPrice = Number(prices.quarterly) || 0;

    // Verificar se os preços são válidos
    if (weeklyPrice <= 0 || isNaN(weeklyPrice)) {
      return {
        monthly: { percentage: 0, amount: 0 },
        quarterly: { percentage: 0, amount: 0 }
      };
    }

    const weeklyTotal = weeklyPrice * 4;
    const quarterlyWeeklyTotal = weeklyPrice * 12;

    // Calcular percentuais com validação
    const monthlyPercentage = weeklyTotal > 0 && monthlyPrice >= 0 
      ? Math.round(((weeklyTotal - monthlyPrice) / weeklyTotal) * 100) 
      : 0;

    const quarterlyPercentage = quarterlyWeeklyTotal > 0 && quarterlyPrice >= 0 
      ? Math.round(((quarterlyWeeklyTotal - quarterlyPrice) / quarterlyWeeklyTotal) * 100) 
      : 0;

    return {
      monthly: {
        percentage: Math.max(0, isNaN(monthlyPercentage) ? 0 : monthlyPercentage),
        amount: Math.max(0, weeklyTotal - monthlyPrice)
      },
      quarterly: {
        percentage: Math.max(0, isNaN(quarterlyPercentage) ? 0 : quarterlyPercentage),
        amount: Math.max(0, quarterlyWeeklyTotal - quarterlyPrice)
      }
    };
  };

  return {
    plans,
    currentPlan,
    upgradePlan: upgradeMutation.mutate,
    isLoading: upgradeMutation.isPending || (ENABLE_ASAAS && asaasLoading),
    calculateSavings,
    stripeSubscriptionPeriod: subscriptionData?.currentPlanInfo?.billing_period || null,
    paymentModal: ENABLE_ASAAS ? paymentModal : null
  };
};
